import React, { useEffect, useState } from 'react';
import { usePortalStore } from '../../store';
import { AlertTriangle, Clock, Zap, Users, Shield, ChevronDown } from 'lucide-react';

const EPIC_STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do', color: 'bg-slate-700 text-slate-300' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-cyan-900 text-cyan-200' },
  { value: 'blocked', label: 'Blocked', color: 'bg-red-900 text-red-200' },
  { value: 'done', label: 'Done', color: 'bg-emerald-900 text-emerald-200' },
  { value: 'deferred', label: 'Deferred', color: 'bg-yellow-900 text-yellow-200' },
];

function statusStyle(status: string): string {
  return EPIC_STATUS_OPTIONS.find(o => o.value === status)?.color ?? 'bg-slate-700 text-slate-300';
}

function riskColor(score: number): string {
  if (score >= 0.7) return 'text-red-400';
  if (score >= 0.35) return 'text-yellow-400';
  return 'text-emerald-400';
}

const EpicStatusDropdown: React.FC<{ epicId: string; currentStatus: string }> = ({ epicId, currentStatus }) => {
  const { updateEpicStatus } = usePortalStore();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSelect = async (status: string) => {
    if (status === currentStatus) { setOpen(false); return; }
    setLoading(true);
    setOpen(false);
    try {
      await updateEpicStatus(epicId, status);
    } finally {
      setLoading(false);
    }
  };

  const current = EPIC_STATUS_OPTIONS.find(o => o.value === currentStatus);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-colors ${current?.color ?? 'bg-slate-700 text-slate-300'} disabled:opacity-50`}
      >
        {loading ? '...' : current?.label ?? currentStatus.replace('_', ' ').toUpperCase()}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden min-w-[130px]">
          {EPIC_STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-700 transition-colors ${opt.value === currentStatus ? 'font-bold' : ''} ${opt.color.replace('bg-', 'text-').split(' ')[0]}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ApprovalsPanel: React.FC = () => {
  const { approvals, loadApprovals, decideApproval } = usePortalStore();
  const [deciding, setDeciding] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const pending = approvals.filter(a => a.decision === null);

  useEffect(() => { loadApprovals(); }, []);

  const handleDecide = async (nmId: string, decision: 'approved' | 'rejected') => {
    setDeciding(null);
    await decideApproval(nmId, decision, reason);
    setReason('');
  };

  if (approvals.length === 0) return null;

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5 text-yellow-400" />
        Governor Approval Queue
        {pending.length > 0 && (
          <span className="ml-auto px-2 py-0.5 bg-yellow-900 text-yellow-200 rounded-full text-xs font-semibold">
            {pending.length} pending
          </span>
        )}
      </h2>
      <div className="space-y-3">
        {approvals.map(item => (
          <div
            key={item.id}
            className={`rounded-lg p-4 border ${
              item.decision === 'approved' ? 'border-emerald-700/50 bg-emerald-950/20' :
              item.decision === 'rejected' ? 'border-red-700/50 bg-red-950/20' :
              'border-yellow-700/50 bg-yellow-950/10'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="text-sm font-semibold text-slate-100">{item.action_class.replace(/_/g, ' ')}</span>
                {item.epic_id && (
                  <span className="ml-2 text-xs text-slate-400">{item.epic_id}</span>
                )}
              </div>
              <span className={`text-xs font-bold ${riskColor(item.risk_score)}`}>
                risk {(item.risk_score * 100).toFixed(0)}%
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-1">Agent: {item.agent_id}</p>
            <p className="text-xs text-slate-500 mb-3">{new Date(item.created_at).toLocaleString()}</p>
            {item.decision ? (
              <div className={`text-xs font-semibold ${item.decision === 'approved' ? 'text-emerald-400' : 'text-red-400'}`}>
                {item.decision.toUpperCase()} {item.decided_at ? `· ${new Date(item.decided_at).toLocaleDateString()}` : ''}
                {item.reason && <span className="text-slate-400 font-normal"> — {item.reason}</span>}
              </div>
            ) : deciding === item.id ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Reason (optional)..."
                  className="w-full px-2 py-1 bg-slate-900 border border-slate-600 rounded text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDecide(item.id, 'approved')}
                    className="flex-1 px-3 py-1 bg-emerald-700 hover:bg-emerald-600 rounded text-xs text-white font-semibold transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleDecide(item.id, 'rejected')}
                    className="flex-1 px-3 py-1 bg-red-700 hover:bg-red-600 rounded text-xs text-white font-semibold transition-colors"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => { setDeciding(null); setReason(''); }}
                    className="px-3 py-1 border border-slate-600 rounded text-xs text-slate-300 hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setDeciding(item.id)}
                className="px-3 py-1 bg-yellow-700 hover:bg-yellow-600 rounded text-xs text-white font-semibold transition-colors"
              >
                Decide
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const OperationsLayer: React.FC = () => {
  const {
    waveStatus,
    actionItems,
    incidents,
    teamCapacity,
    backlogEpics,
    loadWaveStatus,
    loadActionItems,
    loadIncidents,
    loadTeamCapacity,
    loadBacklogEpics,
  } = usePortalStore();

  useEffect(() => {
    loadWaveStatus();
    loadActionItems();
    loadIncidents();
    loadTeamCapacity();
    loadBacklogEpics();
  }, []);

  if (!waveStatus) {
    return <div className="text-slate-400">Loading operations data...</div>;
  }

  const criticalActionItems = actionItems.filter(a => a.priority === 'critical');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-100 mb-2">Operations Dashboard</h1>
        <p className="text-slate-400">Real-time status on backlog, blockers, and team capacity</p>
      </div>

      {/* Wave Status */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">Backlog Progress ({waveStatus.number} delivered)</h2>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            waveStatus.status === 'active' ? 'bg-cyan-900 text-cyan-200' : 'bg-emerald-900 text-emerald-200'
          }`}>
            {waveStatus.status.toUpperCase()}
          </span>
        </div>
        <p className="text-sm text-slate-400 mb-4">{waveStatus.phase}</p>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-300">Overall Progress</span>
            <span className="text-sm font-bold text-cyan-400">{waveStatus.progressPercentage}%</span>
          </div>
          <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 transition-all"
              style={{ width: `${waveStatus.progressPercentage}%` }}
            />
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-400 mt-4">
          <span>Started: {waveStatus.startDate.toLocaleDateString()}</span>
          <span>Completing: {waveStatus.estimatedCompletion.toLocaleDateString()}</span>
        </div>
      </div>

      {/* Critical Alerts */}
      {(criticalActionItems.length > 0 || incidents.length > 0 || waveStatus.blockers.length > 0) && (
        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-300 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Blockers & Alerts
          </h3>
          {incidents.map(incident => (
            <div key={incident.id} className="bg-slate-900/50 rounded p-3 mb-2">
              <div className="flex items-start justify-between mb-1">
                <span className="text-sm font-semibold text-slate-100">{incident.title}</span>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  incident.severity === 'critical' ? 'bg-red-900 text-red-200' : 'bg-orange-900 text-orange-200'
                }`}>
                  {incident.status.toUpperCase()}
                </span>
              </div>
              <p className="text-xs text-slate-400">{incident.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Live Backlog */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Backlog</h2>
        {backlogEpics.length === 0 ? (
          <p className="text-slate-400 text-sm">No epics yet. Add one to get started.</p>
        ) : (
          <div className="space-y-3">
            {backlogEpics.map(epic => (
              <div key={epic.id} className="bg-slate-900/50 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-100 truncate">{epic.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{epic.id} · WSJF {epic.wsjf}</p>
                  </div>
                  <EpicStatusDropdown epicId={epic.id} currentStatus={epic.status} />
                </div>
                {epic.description && (
                  <p className="text-xs text-slate-400 mt-2 line-clamp-2">{epic.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Items */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Action Items
        </h2>
        <div className="space-y-3">
          {actionItems.map(item => (
            <div key={item.id} className={`rounded p-4 ${
              item.status === 'overdue' ? 'bg-red-900/20 border border-red-700/50' :
              item.status === 'pending' ? 'bg-yellow-900/20 border border-yellow-700/50' :
              'bg-slate-900 border border-slate-700'
            }`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-slate-100 text-sm">{item.title}</h3>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  item.status === 'completed' ? 'bg-emerald-900 text-emerald-200' :
                  item.status === 'overdue' ? 'bg-red-900 text-red-200' :
                  'bg-yellow-900 text-yellow-200'
                }`}>
                  {item.status.toUpperCase()}
                </span>
              </div>
              <p className="text-xs text-slate-400">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Team Capacity */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Team Capacity
        </h2>
        <div className="space-y-4">
          {teamCapacity.map(agent => (
            <div key={agent.agentId} className="bg-slate-900/50 rounded p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-100">{agent.name}</h3>
                  <p className="text-xs text-slate-400">{agent.role}</p>
                </div>
                <span className="text-sm font-bold text-cyan-400">{agent.currentCapacity}% available</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-3">
                <div
                  className={`h-full ${agent.currentCapacity > 70 ? 'bg-emerald-500' : agent.currentCapacity > 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${agent.currentCapacity}%` }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                <div><span className="block text-slate-500">Tasks</span><span className="font-semibold text-slate-200">{agent.assignedTasks}</span></div>
                <div><span className="block text-slate-500">Completion</span><span className="font-semibold text-emerald-400">{agent.completionRate}%</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Governor Approval Queue */}
      <ApprovalsPanel />

      {/* Milestones */}
      <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-700/50 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-indigo-200 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Milestones
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Epics Delivered', value: String(waveStatus.number) },
            { label: 'In Progress', value: String(waveStatus.epics.length) },
            { label: 'Overall Progress', value: `${waveStatus.progressPercentage}%` },
            { label: 'Status', value: waveStatus.status.toUpperCase() },
          ].map((m, idx) => (
            <div key={idx} className="bg-slate-900/70 rounded p-4 text-center">
              <div className="text-sm text-slate-400">{m.label}</div>
              <div className="text-lg font-bold text-indigo-300 mt-1">{m.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
